import React, { useEffect, useRef, useState } from 'react';
import { Html5Qrcode } from 'html5-qrcode';

interface QrScannerProps {
  onScan: (value: string) => void;
  active: boolean;
  /** Unique DOM id when multiple scanners exist across pages */
  elementId?: string;
}

const DEFAULT_SCANNER_ELEMENT_ID = 'boarding-qr-reader';

const safeStopScanner = async (scanner: Html5Qrcode | null) => {
  if (!scanner) return;
  try {
    const state = typeof scanner.getState === 'function' ? scanner.getState() : null;
    // 2 = SCANNING, 3 = PAUSED (library enum)
    const canStop =
      state === 2 ||
      state === 3 ||
      (state == null && !!scanner.isScanning);

    if (canStop) {
      await scanner.stop();
    }
  } catch {
    // ignore stop errors during teardown / race with start()
  }
  try {
    scanner.clear();
  } catch {
    // ignore
  }
};

const QrScanner: React.FC<QrScannerProps> = ({
  onScan,
  active,
  elementId = DEFAULT_SCANNER_ELEMENT_ID,
}) => {
  const [error, setError] = useState('');
  const onScanRef = useRef(onScan);
  const lastScanRef = useRef('');
  const scannerRef = useRef<Html5Qrcode | null>(null);

  useEffect(() => {
    onScanRef.current = onScan;
  }, [onScan]);

  useEffect(() => {
    if (!active) {
      void safeStopScanner(scannerRef.current);
      scannerRef.current = null;
      return;
    }

    let cancelled = false;
    let scanner: Html5Qrcode | null = null;

    const start = async () => {
      setError('');
      // Ensure previous instance is fully stopped before creating a new one
      await safeStopScanner(scannerRef.current);
      scannerRef.current = null;

      if (cancelled) return;

      try {
        scanner = new Html5Qrcode(elementId);
        scannerRef.current = scanner;

        await scanner.start(
          { facingMode: 'environment' },
          { fps: 8, qrbox: { width: 240, height: 240 } },
          (decoded) => {
            if (!decoded || decoded === lastScanRef.current) return;
            lastScanRef.current = decoded;
            onScanRef.current(decoded);
            window.setTimeout(() => {
              if (lastScanRef.current === decoded) lastScanRef.current = '';
            }, 4000);
          },
          () => undefined
        );

        // If unmounted / deactivated while starting, tear down immediately
        if (cancelled) {
          await safeStopScanner(scanner);
          if (scannerRef.current === scanner) scannerRef.current = null;
        }
      } catch (e: unknown) {
        if (!cancelled) {
          const insecure =
            typeof window !== 'undefined' &&
            !window.isSecureContext &&
            window.location.hostname !== 'localhost' &&
            window.location.hostname !== '127.0.0.1';

          if (insecure) {
            setError(
              'المتصفح يمنع الكاميرا على روابط http. افتح الصفحة من الكمبيوتر عبر localhost، أو استخدم رابط https، أو أدخل الرمز يدوياً.'
            );
          } else {
            const message =
              e instanceof Error
                ? e.message
                : 'تعذر فتح الكاميرا. يمكنك إدخال الرمز يدوياً.';
            setError(message);
          }
        }
        await safeStopScanner(scanner);
        if (scannerRef.current === scanner) scannerRef.current = null;
      }
    };

    void start();

    return () => {
      cancelled = true;
      const current = scannerRef.current || scanner;
      scannerRef.current = null;
      void safeStopScanner(current);
    };
  }, [active, elementId]);

  return (
    <div className="space-y-2">
      <div
        id={elementId}
        className="overflow-hidden rounded-lg bg-black min-h-[240px] w-full max-w-md mx-auto"
      />
      {error && (
        <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-2 text-center">
          {error}
        </p>
      )}
    </div>
  );
};

export default QrScanner;
